import React, { useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import firebase from "firebase/app";
import { StyledFirebaseAuth } from "react-firebaseui";
import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
} from "@material-ui/core";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import PropTypes from "prop-types";
import queryString from "query-string";

import "./SignInScreen.css";
import useLoginPage from "./hooks/useLoginPage";
import { setLoginPage } from "./actions/loginPage";

const firebaseConfig = {
  apiKey: "AIzaSyDaPNMgYrJYY8FxlRQ4IkikFmzqEqywlQA", //gitleaks baseline
  authDomain: "auth.freshpaint.io",
  projectId: "freshpaint-1",
  storageBucket: "test-project-c1b2f.appspot.com",
  messagingSenderId: "830445107051",
};

firebase.initializeApp(firebaseConfig);

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "100vh",
    display: "flex",
    "@media all and (-ms-high-contrast:none)": {
      height: 0, // IE11 fix
    },
  },
  mainContent: {
    flexGrow: 1,
    maxWidth: "100%",
    overflowX: "hidden",
    paddingTop: 64,
    [theme.breakpoints.down("xs")]: {
      paddingTop: 56,
    },
  },
  root: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
  },
  card: {
    width: "480px",
    maxWidth: "100%",
    overflow: "visible",
    display: "flex",
    position: "relative",
    "& > *": {
      flexGrow: 1,
      flexBasis: "50%",
      width: "50%",
    },
  },
  content: {
    padding: theme.spacing(8, 4, 3, 4),
  },
  media: {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    padding: theme.spacing(3),
    color: theme.palette.common.white,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  iconBackground: {
    backgroundColor: "#EE5757",
    borderRadius: theme.shape.borderRadius,
    position: "absolute",
    top: -32,
    left: theme.spacing(3),
    padding: 14,
    height: 64,
    width: 64,
    fontSize: 32,
  },
  icon: {
    color: theme.palette.common.white,
    position: "absolute",
    width: 36,
    height: 36,
  },
  loginForm: {
    marginTop: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  person: {
    marginTop: theme.spacing(2),
    display: "flex",
  },
  avatar: {
    marginRight: theme.spacing(2),
  },
}));

// The keys are the supported URL slugs for /login/sso/:slug . The values are
// used as the objects in the signInOptions array for firebase
// https://github.com/firebase/firebaseui-web#configure-oauth-providers
const providerMap = {
  "auth0-test": {
    provider: "oidc.auth0-test",
    providerName: "Auth0",
  },
  "modern-health": {
    provider: "saml.modern-health",
    providerName: "Okta",
  },
  cerebral: {
    provider: "saml.cerebral",
    providerName: "Okta",
  },
  "yale-school-of-medicine": {
    provider: "saml.yale-school-of-medicine",
    providerName: "Yale",
  },
  "yale-new-haven-health": {
    provider: "saml.yale-new-haven-health",
    providerName: "Yale New Haven Health",
  },
  "jumpcloud-sso-test": {
    provider: "saml.jumpcloud-sso-test",
    providerName: "Freshpaint Jumpcloud SSO Test",
  },
  "unc-health": {
    provider: "saml.unc-health",
    providerName: "UNC Health",
  },
  "inova-health": {
    provider: "saml.inova",
    providerName: "Inova Health",
  },
  heartland: {
    provider: "saml.heartland",
    providerName: "Heartland Dental",
  },
  jefferson: {
    provider: "saml.jefferson",
    providerName: "Jefferson",
  },
  caresource: {
    provider: "saml.caresource",
    providerName: "Okta",
  },
  alpine: {
    provider: "saml.alpine",
    providerName: "Alpine",
  },
  betabionics: {
    provider: "saml.betabionics",
    providerName: "Beta Bionics",
  },
};

function Login(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { providerName } = useParams();
  const loginPage = useLoginPage();
  const history = useHistory();

  const destination =
    queryString.parse(props.location.search).next || "/events";
  if (props.isSignedIn) {
    return <Redirect to={destination} />;
  }

  // If the user logged in via SSO last time, redirect them to that SSO login
  // page
  if (loginPage !== "/login" && !props.ssoOnly) {
    return <Redirect to={{ pathname: loginPage }} />;
  }

  const onClickReturnToMainSignInPage = () => {
    dispatch(setLoginPage("/login"));
    history.push("/login");
  };

  const shouldUseSSOProvider = props.ssoOnly && providerMap[providerName];

  const uiConfig = {
    // This is a hack so that we can have both oauth and email link providers.
    // Oauth providers like google don't work in safari if you use the redirect
    // flow, but email link doesn't work if you use the popup flow.
    // See https://github.com/firebase/firebaseui-web-react/issues/67
    signInFlow: firebase.auth().isSignInWithEmailLink(window.location.href)
      ? "redirect"
      : "popup",
    credentialHelper: "none",
    signInOptions: shouldUseSSOProvider
      ? [providerMap[providerName]]
      : [
          firebase.auth.GoogleAuthProvider.PROVIDER_ID,
          firebase.auth.GithubAuthProvider.PROVIDER_ID,
          {
            provider: firebase.auth.EmailAuthProvider.PROVIDER_ID,
            signInMethod:
              firebase.auth.EmailAuthProvider.EMAIL_LINK_SIGN_IN_METHOD,
            emailLinkSignIn: function () {
              return {
                url: `${window.location.origin}/login?${queryString.stringify({
                  next: destination,
                })}`,
                handleCodeInApp: true,
              };
            },
          },
        ],
  };

  return (
    <div className={classes.container}>
      <div className={classes.mainContent}>
        <div className={classes.root}>
          <Card className={classes.card}>
            <CardContent className={classes.content}>
              <div className={classes.iconBackground}>
                <PersonAddIcon className={classes.icon} />
              </div>
              <Typography gutterBottom variant="h3">
                Welcome to Freshpaint
              </Typography>
              <Typography variant="subtitle2">
                Codeless data collection. Integrate your entire stack.
              </Typography>
              <Divider className={classes.divider} />
              <StyledFirebaseAuth
                // hack to make the firebase ui reset when navigating from sso login page to main login page
                key={providerName || ""}
                uiConfig={uiConfig}
                firebaseAuth={firebase.auth()}
              />
              <Divider className={classes.divider} />
              <Typography variant="subtitle2">
                By continuing, you are indicating that you accept our{" "}
                <a href="https://www.freshpaint.io/terms">Terms of Service</a>.
              </Typography>
              {shouldUseSSOProvider && (
                <>
                  <br />
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onClickReturnToMainSignInPage}
                  >
                    Return to the main sign-in page
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

Login.propTypes = {
  isSignedIn: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  ssoOnly: PropTypes.bool,
};

export default Login;
